import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getFlagEmoji } from '@/utils/get-flag';
import type { Country } from '@/types/country';

export function useCountryName() {
  const { t } = useTranslation();

  return useCallback(
    (country: Country | null | undefined): string => {
      if (!country) return '';
      const flag = getFlagEmoji(country.id);

      // Prevent issues where cached data might still contain the formatted flag
      const fallbackText =
        flag && country.name.startsWith(flag) ? country.name.slice(flag.length).trim() : country.name;

      const translatedText = t(`countries.${country.id.toLowerCase()}`, fallbackText);

      return flag ? `${flag} ${translatedText}` : translatedText;
    },
    [t]
  );
}

export function useCountryNameById(countries?: Country[]) {
  const getCountryName = useCountryName();
  
  return useCallback(
    (id: string | null | undefined): string => {
      if (!id) return '';
      if (!countries?.length) return id;
      
      const country = countries.find(c => c.id === id);
      return country ? getCountryName(country) : id;
    },
    [countries, getCountryName]
  );
}
